import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import React, { forwardRef, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { ResourcedEmoji } from '@atlaskit/emoji';
import { Inline, xcss, Box, Flex } from '@atlaskit/primitives';
import { messages } from '../../shared/i18n';
import { Counter } from '../Counter';
import { ReactionButton } from '../Reaction';
var emojiStyle = xcss({
  transformOrigin: 'center center 0',
  // eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
  lineHeight: '12px'
});
var buttonStyle = xcss({
  paddingTop: 'space.050',
  paddingRight: 'space.050',
  paddingBottom: 'space.050',
  paddingLeft: 'space.100'
});
var compactButtonStyle = xcss({
  height: '20px',
  alignItems: 'center',
  marginTop: 'space.075'
});
var compactPaddingStyles = xcss({
  paddingTop: 'space.025'
});
var opaqueBackgroundStyles = xcss({
  backgroundColor: 'elevation.surface',
  ':hover': {
    backgroundColor: 'elevation.surface.hovered'
  },
  ':active': {
    backgroundColor: 'elevation.surface.pressed'
  }
});
var containerStyle = xcss({
  position: 'relative'
});
var hideBorderStyle = xcss({
  border: 'none'
});
/**
 * Test id for summary reaction wrapper button
 */
export var RENDER_SUMMARY_BUTTON_TESTID = 'reaction-summary-button';

/**
 * Test id for emojis displayed inside summary button. All emoji's in the summary button will have this test id
 */
export var RENDER_SUMMARY_EMOJI_TESTID = 'summary-emoji-display';

// forwardRef is used here so that the parent popup component can properly interact with the button
export var ReactionSummaryButton = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var emojiProvider = _ref.emojiProvider,
    _ref$reactions = _ref.reactions,
    reactions = _ref$reactions === void 0 ? [] : _ref$reactions,
    _ref$emojisToShow = _ref.emojisToShow,
    emojisToShow = _ref$emojisToShow === void 0 ? 3 : _ref$emojisToShow,
    onClick = _ref.onClick,
    _ref$showOpaqueBackgr = _ref.showOpaqueBackground,
    showOpaqueBackground = _ref$showOpaqueBackgr === void 0 ? false : _ref$showOpaqueBackgr,
    _ref$subtleReactionsS = _ref.subtleReactionsSummaryAndPicker,
    subtleReactionsSummaryAndPicker = _ref$subtleReactionsS === void 0 ? false : _ref$subtleReactionsS,
    _ref$useCompactStyles = _ref.useCompactStyles,
    useCompactStyles = _ref$useCompactStyles === void 0 ? false : _ref$useCompactStyles,
    _ref$useButtonAlignme = _ref.useButtonAlignmentStyling,
    useButtonAlignmentStyling = _ref$useButtonAlignme === void 0 ? false : _ref$useButtonAlignme;
  var intl = useIntl();

  // Helper function to sort reactions by count and return top N
  var getTopReactions = function getTopReactions(reactions, topN) {
    return _toConsumableArray(reactions).sort(function (a, b) {
      return b.count - a.count;
    }).slice(0, topN);
  };
  var totalReactionsCount = useMemo(function () {
    return reactions.reduce(function (accum, current) {
      return accum += (current === null || current === void 0 ? void 0 : current.count) || 0;
    }, 0);
  }, [reactions]);
  var topReactions = useMemo(function () {
    return getTopReactions(reactions, emojisToShow);
  }, [emojisToShow, reactions]);
  var buttonStyles = showOpaqueBackground ? [opaqueBackgroundStyles] : [];
  var subtleSummaryStyles = subtleReactionsSummaryAndPicker ? [hideBorderStyle] : [];
  var compactButtonStyles = useCompactStyles ? [compactButtonStyle] : [];
  return /*#__PURE__*/React.createElement(Flex, {
    xcss: containerStyle,
    ref: ref,
    alignItems: "center",
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(ReactionButton, {
    onClick: onClick,
    testId: RENDER_SUMMARY_BUTTON_TESTID,
    ariaLabel: intl.formatMessage(messages.summary),
    additionalStyles: [].concat(buttonStyles, subtleSummaryStyles, compactButtonStyles)
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "space.050",
    xcss: useCompactStyles ? [buttonStyle, compactPaddingStyles] : buttonStyle,
    testId: "reaction-summary-wrapper"
  }, topReactions.map(function (reaction) {
    return /*#__PURE__*/React.createElement(Box, {
      xcss: emojiStyle,
      testId: RENDER_SUMMARY_EMOJI_TESTID
    }, /*#__PURE__*/React.createElement(ResourcedEmoji, {
      key: reaction.emojiId,
      emojiProvider: emojiProvider,
      emojiId: {
        id: reaction.emojiId,
        shortName: ''
      },
      fitToHeight: useCompactStyles ? 12 : 16
    }));
  })), /*#__PURE__*/React.createElement(Counter, {
    value: totalReactionsCount,
    useDarkerFont: useCompactStyles || useButtonAlignmentStyling,
    useUpdatedStyles: !useCompactStyles && useButtonAlignmentStyling
  })));
});